import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, WifiIcon,
  StopIcon, ArrowPathIcon, UserGroupIcon,
  PencilSquareIcon, XMarkIcon, CreditCardIcon,
  AcademicCapIcon, TrashIcon, WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .bm * { box-sizing:border-box; }
  .bm { font-family:'Sora',sans-serif; color:#1a1d2e; animation:bmFade .4s ease both; }
  @keyframes bmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .bm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .bm-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .bm-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; box-shadow:0 4px 14px rgba(16,201,142,.35); transform:translateY(-1px); }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  .page-tabs { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:1.4rem; border-bottom:2px solid #f0f2f8; padding-bottom:0; }
  .page-tab  { padding:9px 18px; border:none; background:none; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; color:#9094a8; border-bottom:2px solid transparent; margin-bottom:-2px; transition:all .15s; display:inline-flex; align-items:center; gap:6px; }
  .page-tab svg { width:15px; height:15px; stroke-width:1.8; }
  .page-tab:hover { color:#3b6ef8; }
  .page-tab.active { color:#3b6ef8; border-bottom-color:#3b6ef8; }
  .status-card { border-radius:16px; padding:1.2rem 1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
  .status-card.connected    { background:linear-gradient(135deg,#ecfdf5,#d1fae5); border:1px solid #a7f3d0; }
  .status-card.disconnected { background:linear-gradient(135deg,#fef2f2,#fee2e2); border:1px solid #fecaca; }
  .status-card.polling      { background:linear-gradient(135deg,#eef2ff,#dbeafe); border:1px solid #c7d2fe; }
  .sc-left { display:flex; align-items:center; gap:12px; }
  .sc-dot  { width:12px; height:12px; border-radius:50%; flex-shrink:0; }
  .sc-dot.green { background:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,.2); animation:pulse 2s infinite; }
  .sc-dot.red   { background:#ef4444; }
  .sc-dot.blue  { background:#3b6ef8; box-shadow:0 0 0 3px rgba(59,110,248,.2); animation:pulse 2s infinite; }
  @keyframes pulse { 0%,100%{box-shadow:0 0 0 3px rgba(16,201,142,.2)} 50%{box-shadow:0 0 0 6px rgba(16,201,142,.1)} }
  .sc-label { font-size:.9rem; font-weight:700; color:#0f1123; }
  .sc-sub   { font-size:.72rem; color:#6b7194; margin-top:2px; }
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; padding:18px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.span2 { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus { border-color:#3b6ef8; background:white; }
  .form-input.mono { font-family:'JetBrains Mono',monospace; }
  .form-hint { font-size:.68rem; color:#b0b5cc; }
  .form-actions { display:flex; gap:8px; padding:0 18px 18px; flex-wrap:wrap; }
  .test-result { margin:0 18px 18px; padding:12px 14px; border-radius:10px; font-size:.8rem; }
  .test-result.success { background:#ecfdf5; border:1px solid #a7f3d0; color:#065f46; }
  .test-result.error   { background:#fef2f2; border:1px solid #fecaca; color:#991b1b; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:10px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .assigned-badge   { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .unassigned-badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#fef2f2; color:#ef4444; }
  .rfid-badge { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#eef2ff; color:#3b6ef8; font-family:'JetBrains Mono',monospace; }
  .rfid-badge svg { width:11px; height:11px; stroke-width:2; }
  .fp-badge { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#f3f0ff; color:#8b5cf6; }
  .search-wrap { position:relative; max-width:240px; }
  .search-wrap svg { position:absolute; left:10px; top:50%; transform:translateY(-50%); width:14px; height:14px; stroke:#9094a8; pointer-events:none; }
  .search-input { font-family:'Sora',sans-serif; font-size:.78rem; width:100%; padding:7px 10px 7px 30px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; outline:none; }
  .search-input:focus { border-color:#3b6ef8; background:white; }
  .bm-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .bm-loading { display:flex; align-items:center; justify-content:center; height:150px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:440px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:18px; flex-wrap:wrap; }
  .assign-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .assign-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .assign-input,.assign-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .assign-input:focus,.assign-select:focus { border-color:#3b6ef8; background:white; }
  .act-btn { width:30px; height:30px; border-radius:8px; border:1px solid #e8ebf4; background:#f8f9fc; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; margin-left:4px; transition:.13s; }
  .act-btn svg { width:13px; height:13px; stroke-width:1.8; }
  .act-btn.edit { color:#3b6ef8; } .act-btn.edit:hover { background:#eef2ff; border-color:#3b6ef8; }
  .act-btn.del  { color:#ef4444; } .act-btn.del:hover  { background:#fef2f2; border-color:#fecaca; }
  .bm-cards { display:none; padding:10px; }
  .bm-card  { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .bmc-top  { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .bmc-row  { display:flex; justify-content:space-between; align-items:center; padding:5px 0; gap:10px; border-bottom:1px solid #f5f6fb; font-size:.78rem; }
  .bmc-row:last-of-type { border-bottom:none; }
  .bmc-label { color:#9094a8; font-weight:600; font-size:.7rem; text-transform:uppercase; letter-spacing:.05em; white-space:nowrap; }
  .bmc-actions { display:flex; gap:6px; margin-top:10px; padding-top:8px; border-top:1px solid #f0f2f8; }
  @media (max-width:900px) { .two-col { grid-template-columns:1fr; } }
  @media (max-width:768px) {
    .bm-title { font-size:1.1rem; }
    .status-card { flex-direction:column; align-items:flex-start; }
    .form-grid { grid-template-columns:1fr; padding:14px; }
    .form-field.span2 { grid-column:1; }
    .form-actions { flex-direction:column; padding:0 14px 14px; }
    .form-actions .btn { width:100%; justify-content:center; }
    .modal { padding:18px; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
    .bm-tbl-wrap { display:none !important; }
    .bm-cards { display:block; }
  }
`;

const BiometricSettings = () => {
  const [activeTab,    setActiveTab]    = useState('machine');
  const [status,       setStatus]       = useState(null);
  const [config,       setConfig]       = useState({ ip:'192.168.10.201', port:'4370', pollInterval:'60' });
  const [testResult,   setTestResult]   = useState(null);
  const [testing,      setTesting]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [stopping,     setStopping]     = useState(false);
  const [machineUsers, setMachineUsers] = useState([]);
  const [teachers,     setTeachers]     = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignModal,  setAssignModal]  = useState(null);
  const [assignForm,   setAssignForm]   = useState({ teacherId:'' });
  const [assigning,    setAssigning]    = useState(false);

  // RFID Teacher state
  const [rfidTeachers,    setRFIDTeachers]    = useState([]);
  const [rfidTeachSearch, setRFIDTeachSearch] = useState('');
  const [rfidTeachModal,  setRFIDTeachModal]  = useState(null);
  const [rfidTeachCard,   setRFIDTeachCard]   = useState('');
  const [savingRFIDTeach, setSavingRFIDTeach] = useState(false);

  // RFID Student state
  const [rfidStudents,   setRFIDStudents]   = useState([]);
  const [rfidStudSearch, setRFIDStudSearch] = useState('');
  const [rfidStudModal,  setRFIDStudModal]  = useState(null);
  const [rfidStudCard,   setRFIDStudCard]   = useState('');
  const [savingRFIDStud, setSavingRFIDStud] = useState(false);
  const [classes,        setClasses]        = useState([]);

  // RFID Non-Portal Staff state
  const [rfidStaff,         setRFIDStaff]         = useState([]);
  const [rfidStaffSearch,   setRFIDStaffSearch]   = useState('');
  const [rfidStaffModal,    setRFIDStaffModal]     = useState(null);
  const [rfidStaffCard,     setRFIDStaffCard]      = useState('');
  const [savingRFIDStaff,   setSavingRFIDStaff]   = useState(false);
  const [filterDesignation, setFilterDesignation] = useState('');
  const DESIGNATIONS = ['peon','cleaner','security','driver','cook','gardener','other'];

  const [filterClass,   setFilterClass]   = useState('');
  const [filterSection, setFilterSection] = useState('');

  // RFID Portal Staff (coordinator + finance) state
  const [rfidPortalStaff,  setRFIDPortalStaff]  = useState([]);
  const [rfidPortalSearch, setRFIDPortalSearch] = useState('');
  const [rfidPortalModal,  setRFIDPortalModal]  = useState(null);
  const [rfidPortalCard,   setRFIDPortalCard]   = useState('');
  const [savingRFIDPortal, setSavingRFIDPortal] = useState(false);
  const [filterPortalRole, setFilterPortalRole] = useState('');

  useEffect(() => {
    fetchStatus();
    api.get('/admin/teachers').then(r => setTeachers(r.data?.teachers || r.data || [])).catch(()=>{});
    api.get('/admin/classes').then(r => setClasses(Array.isArray(r.data) ? r.data : [])).catch(()=>{});
  }, []);

  useEffect(() => {
    if (activeTab === 'rfid-teachers')     loadRFIDTeachers();
    if (activeTab === 'rfid-students')     loadRFIDStudents();
    if (activeTab === 'rfid-staff')        loadRFIDStaff();
    if (activeTab === 'rfid-portal-staff') loadRFIDPortalStaff();
  }, [activeTab, filterClass, filterSection, filterDesignation, filterPortalRole]);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get('/admin/biometric/status');
      setStatus(data);
      if (data.config?.ip) setConfig({ ip: data.config.ip, port: String(data.config.port || 4370), pollInterval: String(data.config.pollInterval || 60) });
    } catch {}
  };

  const loadRFIDTeachers = async () => {
    try { const { data } = await api.get('/admin/biometric/rfid-teachers'); setRFIDTeachers(Array.isArray(data) ? data : []); } catch {}
  };

  const loadRFIDStaff = async () => {
    try {
      const params = filterDesignation ? `?designation=${filterDesignation}` : '';
      const { data } = await api.get('/admin/biometric/rfid-staff' + params);
      setRFIDStaff(Array.isArray(data) ? data : []);
    } catch {}
  };

  const loadRFIDStudents = async () => {
    try {
      const params = [];
      if (filterClass)   params.push('classId=' + filterClass);
      if (filterSection) params.push('section=' + filterSection);
      const q = params.length ? '?' + params.join('&') : '';
      const { data } = await api.get('/admin/biometric/rfid-students' + q);
      setRFIDStudents(Array.isArray(data) ? data : []);
    } catch {}
  };

  const loadRFIDPortalStaff = async () => {
    try {
      const params = filterPortalRole ? `?role=${filterPortalRole}` : '';
      const { data } = await api.get('/admin/biometric/rfid-portal-staff' + params);
      setRFIDPortalStaff(Array.isArray(data) ? data : []);
    } catch {}
  };

  // ── Machine tab handlers ────────────────────────────────────────────────────
  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/admin/biometric/test', { ip: config.ip, port: Number(config.port) });
      setTestResult({ success: true, message: `Connected! ${data.info ? `Firmware: ${data.info.firmwareVersion || 'OK'}` : ''}` });
    } catch (err) { setTestResult({ success: false, message: err.response?.data?.message || 'Connection failed' }); }
    finally { setTesting(false); }
  };

  const handleConfigure = async () => {
    setSaving(true);
    try {
      await api.post('/admin/biometric/configure', { ip: config.ip, port: Number(config.port), pollInterval: Number(config.pollInterval) });
      toast.success('Biometric machine configured and polling started');
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleStop = async () => {
    setStopping(true);
    try { await api.post('/admin/biometric/stop'); toast.success('Biometric polling stopped'); fetchStatus(); }
    catch { toast.error('Failed'); }
    finally { setStopping(false); }
  };

  const loadMachineUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get(`/admin/biometric/users?ip=${config.ip}&port=${config.port}`);
      setMachineUsers(data.users || []);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load machine users'); }
    finally { setLoadingUsers(false); }
  };

  const handleAssign = async () => {
    if (!assignForm.teacherId) { toast.error('Select a teacher'); return; }
    setAssigning(true);
    try {
      const { data } = await api.put('/admin/biometric/assign', { teacherId: assignForm.teacherId, biometricId: assignModal.uid || assignModal.userId });
      toast.success(data?.message || 'Biometric ID assigned to teacher');
      setAssignModal(null);
      loadMachineUsers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setAssigning(false); }
  };

  // ── RFID Teacher handlers ───────────────────────────────────────────────────
  const handleAssignRFIDTeacher = async () => {
    if (!rfidTeachCard.trim()) { toast.error('Enter RFID card number'); return; }
    setSavingRFIDTeach(true);
    try {
      const { data } = await api.put('/admin/biometric/assign-rfid-teacher', { teacherId: rfidTeachModal._id, rfidCard: rfidTeachCard.trim() });
      toast.success(data?.message || 'RFID card assigned to teacher');
      setRFIDTeachModal(null); setRFIDTeachCard('');
      loadRFIDTeachers();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingRFIDTeach(false); }
  };

  const handleRemoveRFIDTeacher = async (teacherId) => {
    if (!window.confirm('Remove RFID card from this teacher?')) return;
    try {
      await api.delete('/admin/biometric/assign-rfid-teacher/' + teacherId);
      toast.success('RFID card removed');
      loadRFIDTeachers();
    } catch { toast.error('Failed'); }
  };

  // ── RFID Student handlers ───────────────────────────────────────────────────
  const handleAssignRFIDStudent = async () => {
    if (!rfidStudCard.trim()) { toast.error('Enter RFID card number'); return; }
    setSavingRFIDStud(true);
    try {
      await api.put('/admin/biometric/assign-rfid-student', { studentId: rfidStudModal._id, rfidCard: rfidStudCard.trim() });
      toast.success('RFID card assigned to student');
      setRFIDStudModal(null); setRFIDStudCard('');
      loadRFIDStudents();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingRFIDStud(false); }
  };

  const handleRemoveRFIDStudent = async (studentId) => {
    if (!window.confirm('Remove RFID card from this student?')) return;
    try {
      await api.delete('/admin/biometric/assign-rfid-student/' + studentId);
      toast.success('RFID card removed');
      loadRFIDStudents();
    } catch { toast.error('Failed'); }
  };

  // ── RFID Staff handlers ─────────────────────────────────────────────────────
  const handleAssignRFIDStaff = async () => {
    if (!rfidStaffCard.trim()) { toast.error('Enter RFID card number'); return; }
    setSavingRFIDStaff(true);
    try {
      await api.put('/admin/biometric/assign-rfid-staff', { staffId: rfidStaffModal._id, rfidCard: rfidStaffCard.trim() });
      toast.success('RFID card assigned to staff member');
      setRFIDStaffModal(null); setRFIDStaffCard('');
      loadRFIDStaff();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingRFIDStaff(false); }
  };

  const handleRemoveRFIDStaff = async (staffId) => {
    if (!window.confirm('Remove RFID card from this staff member?')) return;
    try {
      await api.delete('/admin/biometric/assign-rfid-staff/' + staffId);
      toast.success('RFID card removed');
      loadRFIDStaff();
    } catch { toast.error('Failed'); }
  };

  // ── RFID Portal Staff handlers ──────────────────────────────────────────────
  const handleAssignRFIDPortalStaff = async () => {
    if (!rfidPortalCard.trim()) { toast.error('Enter RFID card number'); return; }
    setSavingRFIDPortal(true);
    try {
      await api.put('/admin/biometric/assign-rfid-portal-staff', { userId: rfidPortalModal._id, rfidCard: rfidPortalCard.trim() });
      toast.success('RFID card assigned to ' + rfidPortalModal.name);
      setRFIDPortalModal(null); setRFIDPortalCard('');
      loadRFIDPortalStaff();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSavingRFIDPortal(false); }
  };

  const handleRemoveRFIDPortalStaff = async (userId) => {
    if (!window.confirm('Remove RFID card from this user?')) return;
    try {
      await api.delete('/admin/biometric/assign-rfid-portal-staff/' + userId);
      toast.success('RFID card removed');
      loadRFIDPortalStaff();
    } catch { toast.error('Failed'); }
  };

  const isPolling   = status?.isPolling;
  const isConn      = status?.isConnected;
  const lastPull    = status?.lastPullTime ? new Date(status.lastPullTime).toLocaleTimeString() : '—';

  const filteredRFIDTeachers = rfidTeachers.filter(t =>
    !rfidTeachSearch || t.user?.name?.toLowerCase().includes(rfidTeachSearch.toLowerCase()) || t.employeeId?.toLowerCase().includes(rfidTeachSearch.toLowerCase())
  );
  const filteredRFIDStaff = rfidStaff.filter(s =>
    !rfidStaffSearch || s.name?.toLowerCase().includes(rfidStaffSearch.toLowerCase()) || s.employeeId?.toLowerCase().includes(rfidStaffSearch.toLowerCase())
  );
  const filteredRFIDStudents = rfidStudents.filter(s =>
    !rfidStudSearch || s.user?.name?.toLowerCase().includes(rfidStudSearch.toLowerCase()) || s.admissionNo?.toLowerCase().includes(rfidStudSearch.toLowerCase())
  );
  const filteredRFIDPortalStaff = rfidPortalStaff.filter(u =>
    !rfidPortalSearch || u.name?.toLowerCase().includes(rfidPortalSearch.toLowerCase()) || u.email?.toLowerCase().includes(rfidPortalSearch.toLowerCase())
  );

  return (
    <div className="bm">
      <style>{style}</style>

      <div className="bm-header">
        <div>
          <div className="bm-title">Biometric & RFID</div>
          <div className="bm-sub">Configure ZKTeco K50 — fingerprint + RFID for all staff, RFID for students</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={fetchStatus}><ArrowPathIcon />Refresh</button>
      </div>

      <div className="page-tabs">
        <button className={`page-tab ${activeTab==='machine'?'active':''}`} onClick={() => setActiveTab('machine')}>
          <WifiIcon />Machine Setup
        </button>
        <button className={`page-tab ${activeTab==='fingerprint'?'active':''}`} onClick={() => setActiveTab('fingerprint')}>
          <UserGroupIcon />Fingerprint Users
        </button>
        <button className={`page-tab ${activeTab==='rfid-teachers'?'active':''}`} onClick={() => setActiveTab('rfid-teachers')}>
          <CreditCardIcon />Teacher RFID
        </button>
        <button className={`page-tab ${activeTab==='rfid-portal-staff'?'active':''}`} onClick={() => setActiveTab('rfid-portal-staff')}>
          <UserGroupIcon />Coordinator &amp; Finance RFID
        </button>
        <button className={`page-tab ${activeTab==='rfid-students'?'active':''}`} onClick={() => setActiveTab('rfid-students')}>
          <AcademicCapIcon />Student RFID
        </button>
        <button className={`page-tab ${activeTab==='rfid-staff'?'active':''}`} onClick={() => setActiveTab('rfid-staff')}>
          <WrenchScrewdriverIcon />Non-Portal Staff RFID
        </button>
      </div>

      {/* ══ MACHINE SETUP TAB ══ */}
      {activeTab === 'machine' && (
        <>
          <div className={`status-card ${isPolling ? 'polling' : isConn ? 'connected' : 'disconnected'}`}>
            <div className="sc-left">
              <div className={`sc-dot ${isPolling ? 'blue' : isConn ? 'green' : 'red'}`} />
              <div>
                <div className="sc-label">{isPolling ? '🔄 Polling Active' : isConn ? '✅ Connected' : '❌ Not Connected'}</div>
                <div className="sc-sub">
                  {isPolling ? `Auto-syncing every ${status?.config?.pollInterval || 60}s · Last pull: ${lastPull}`
                    : isConn ? 'Connected but not polling'
                    : 'Machine not configured or unreachable'}
                </div>
              </div>
            </div>
            {isPolling && (
              <button className="btn btn-red btn-sm" onClick={handleStop} disabled={stopping}>
                <StopIcon />{stopping ? 'Stopping…' : 'Stop Polling'}
              </button>
            )}
          </div>

          <div className="two-col">
            <div className="panel">
              <div className="panel-head">
                <div><div className="panel-title">Machine Configuration</div><div className="panel-sub">ZKTeco K50 · TCP/IP</div></div>
              </div>
              <div className="form-grid">
                <div className="form-field span2">
                  <label className="form-label">Machine IP Address *</label>
                  <input type="text" className="form-input mono" value={config.ip} onChange={e => setConfig({...config, ip:e.target.value})} placeholder="192.168.1.201" />
                  <span className="form-hint">Find on machine: Menu → Comm → Ethernet → IP Address</span>
                </div>
                <div className="form-field">
                  <label className="form-label">Port</label>
                  <input type="number" className="form-input mono" value={config.port} onChange={e => setConfig({...config, port:e.target.value})} placeholder="4370" />
                  <span className="form-hint">Default: 4370</span>
                </div>
                <div className="form-field">
                  <label className="form-label">Poll Every (seconds)</label>
                  <input type="number" className="form-input mono" value={config.pollInterval} onChange={e => setConfig({...config, pollInterval:e.target.value})} placeholder="60" min="10" />
                  <span className="form-hint">How often to check for new punches</span>
                </div>
              </div>
              {testResult && (
                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                  {testResult.success ? '✅ ' : '❌ '}{testResult.message}
                </div>
              )}
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={handleTest} disabled={testing}><WifiIcon />{testing ? 'Testing…' : 'Test Connection'}</button>
                <button className="btn btn-green" onClick={handleConfigure} disabled={saving}><CheckCircleIcon />{saving ? 'Starting…' : isPolling ? 'Update & Restart' : 'Start Polling'}</button>
              </div>
            </div>

            <div className="panel">
              <div className="panel-head"><div className="panel-title">How It Works</div></div>
              <div style={{padding:'16px 18px',fontSize:'.8rem',color:'#4a5075',lineHeight:1.8}}>
                <div style={{marginBottom:10}}><strong style={{color:'#0f1123'}}>Teachers — Fingerprint or RFID</strong><br/>First tap = Check In · Second tap = Check Out</div>
                <div style={{marginBottom:10}}><strong style={{color:'#0f1123'}}>Coordinator & Finance — RFID</strong><br/>Same as teachers — check in/out via RFID card</div>
                <div style={{marginBottom:10}}><strong style={{color:'#0f1123'}}>Non-Portal Staff — RFID</strong><br/>Check in/out tracked via StaffAttendance</div>
                <div style={{marginBottom:10}}><strong style={{color:'#0f1123'}}>Students — RFID Entry Only</strong><br/>Tap card at gate → entry logged. No exit tracking.</div>
                <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:'8px 12px',fontSize:'.75rem',color:'#92400e'}}>
                  ⚠️ Assign RFID cards in the respective RFID tabs above
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══ FINGERPRINT USERS TAB ══ */}
      {activeTab === 'fingerprint' && (
        <div className="panel">
          <div className="panel-head">
            <div><div className="panel-title">Machine Enrolled Users</div><div className="panel-sub">Match fingerprint IDs to teacher accounts</div></div>
            <button className="btn btn-ghost btn-sm" onClick={loadMachineUsers} disabled={loadingUsers}>
              <UserGroupIcon />{loadingUsers ? 'Loading…' : 'Load from Machine'}
            </button>
          </div>
          {loadingUsers ? (
            <div className="bm-loading"><div className="spinner"/>Reading from machine…</div>
          ) : machineUsers.length === 0 ? (
            <div className="bm-empty">Click "Load from Machine" to see enrolled fingerprints.<br/>Then assign each to a teacher account.</div>
          ) : (
            <>
            <div className="bm-tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr><th>Machine ID</th><th>Name on Machine</th><th>Assigned Teacher</th><th>Actions</th></tr></thead>
                <tbody>
                  {machineUsers.map((u, i) => (
                    <tr key={i}>
                      <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600}}>{u.uid || u.userId}</span></td>
                      <td style={{fontSize:'.8rem',color:'#1a1d2e'}}>{u.name || '—'}</td>
                      <td>{u.teacher ? <span className="assigned-badge">✓ {u.teacher.user?.name || u.teacher.employeeId}</span> : <span className="unassigned-badge">Not assigned</span>}</td>
                      <td><button className="btn btn-ghost btn-sm" onClick={() => { setAssignModal(u); setAssignForm({ teacherId: u.teacher?._id || '' }); }}><PencilSquareIcon />Assign</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bm-cards">
              {machineUsers.map((u, i) => (
                <div key={i} className="bm-card">
                  <div className="bmc-top">
                    <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,fontSize:'.82rem'}}>{u.uid || u.userId}</span>
                    {u.teacher ? <span className="assigned-badge">✓ {u.teacher.user?.name || u.teacher.employeeId}</span> : <span className="unassigned-badge">Not assigned</span>}
                  </div>
                  <div className="bmc-row"><span className="bmc-label">Name on Machine</span><span>{u.name || '—'}</span></div>
                  <div className="bmc-actions">
                    <button className="btn btn-ghost btn-sm" style={{flex:1,justifyContent:'center'}} onClick={() => { setAssignModal(u); setAssignForm({ teacherId: u.teacher?._id || '' }); }}><PencilSquareIcon />Assign</button>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* ══ RFID TEACHERS TAB ══ */}
      {activeTab === 'rfid-teachers' && (
        <div className="panel">
          <div className="panel-head">
            <div><div className="panel-title">Teacher RFID Cards</div><div className="panel-sub">Assign RFID cards to teachers as an alternative to fingerprint</div></div>
            <div className="search-wrap">
              <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input className="search-input" placeholder="Search teachers…" value={rfidTeachSearch} onChange={e => setRFIDTeachSearch(e.target.value)} />
            </div>
          </div>
          {filteredRFIDTeachers.length === 0 ? (
            <div className="bm-empty">No teachers found.</div>
          ) : (
            <>
            <div className="bm-tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr><th>Teacher</th><th>Employee ID</th><th>Fingerprint ID</th><th>RFID Card</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredRFIDTeachers.map(t => (
                    <tr key={t._id}>
                      <td style={{fontWeight:600}}>{t.user?.name}</td>
                      <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>{t.employeeId}</span></td>
                      <td>{t.biometricId ? <span className="fp-badge">👆 {t.biometricId}</span> : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}</td>
                      <td>
                        {t.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{t.rfidCard}</span>
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}
                      </td>
                      <td>
                        <button className="act-btn edit" onClick={() => { setRFIDTeachModal(t); setRFIDTeachCard(t.rfidCard || ''); }}><PencilSquareIcon /></button>
                        {t.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDTeacher(t._id)}><TrashIcon /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bm-cards">
              {filteredRFIDTeachers.map(t => (
                <div key={t._id} className="bm-card">
                  <div className="bmc-top">
                    <span style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{t.user?.name}</span>
                    {t.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{t.rfidCard}</span> : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}
                  </div>
                  <div className="bmc-row"><span className="bmc-label">Employee ID</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>{t.employeeId}</span></div>
                  <div className="bmc-row"><span className="bmc-label">Fingerprint ID</span>{t.biometricId ? <span className="fp-badge">👆 {t.biometricId}</span> : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}</div>
                  <div className="bmc-actions">
                    <button className="act-btn edit" onClick={() => { setRFIDTeachModal(t); setRFIDTeachCard(t.rfidCard || ''); }}><PencilSquareIcon /></button>
                    {t.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDTeacher(t._id)}><TrashIcon /></button>}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* ══ RFID PORTAL STAFF TAB (coordinator + finance) ══ */}
      {activeTab === 'rfid-portal-staff' && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Coordinator &amp; Finance RFID Cards</div>
              <div className="panel-sub">Assign RFID cards for auto check-in/out — same logic as teachers</div>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <select style={{fontFamily:'Sora,sans-serif',fontSize:'.78rem',padding:'7px 11px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none'}}
                value={filterPortalRole} onChange={e => setFilterPortalRole(e.target.value)}>
                <option value="">All Roles</option>
                <option value="coordinator">Coordinator</option>
                <option value="finance">Finance</option>
              </select>
              <div className="search-wrap">
                <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input className="search-input" placeholder="Search…" value={rfidPortalSearch} onChange={e => setRFIDPortalSearch(e.target.value)} />
              </div>
            </div>
          </div>
          {filteredRFIDPortalStaff.length === 0 ? (
            <div className="bm-empty">No coordinator or finance members found.</div>
          ) : (
            <>
            <div className="bm-tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr><th>Name</th><th>Role</th><th>Email</th><th>RFID Card</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredRFIDPortalStaff.map(u => (
                    <tr key={u._id}>
                      <td style={{fontWeight:600}}>{u.name}</td>
                      <td>
                        <span style={{fontSize:'.72rem',background:u.role==='coordinator'?'#f3f0ff':'#fffbeb',color:u.role==='coordinator'?'#8b5cf6':'#f59e0b',padding:'2px 9px',borderRadius:99,fontWeight:600,textTransform:'capitalize'}}>
                          {u.role}
                        </span>
                      </td>
                      <td style={{fontSize:'.74rem',color:'#9094a8'}}>{u.email}</td>
                      <td>
                        {u.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{u.rfidCard}</span>
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}
                      </td>
                      <td>
                        <button className="act-btn edit" onClick={() => { setRFIDPortalModal(u); setRFIDPortalCard(u.rfidCard || ''); }}><PencilSquareIcon /></button>
                        {u.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDPortalStaff(u._id)}><TrashIcon /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bm-cards">
              {filteredRFIDPortalStaff.map(u => (
                <div key={u._id} className="bm-card">
                  <div className="bmc-top">
                    <span style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{u.name}</span>
                    <span style={{fontSize:'.72rem',background:u.role==='coordinator'?'#f3f0ff':'#fffbeb',color:u.role==='coordinator'?'#8b5cf6':'#f59e0b',padding:'2px 9px',borderRadius:99,fontWeight:600,textTransform:'capitalize'}}>{u.role}</span>
                  </div>
                  <div className="bmc-row"><span className="bmc-label">Email</span><span style={{fontSize:'.74rem',color:'#9094a8'}}>{u.email}</span></div>
                  <div className="bmc-row"><span className="bmc-label">RFID Card</span>{u.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{u.rfidCard}</span> : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}</div>
                  <div className="bmc-actions">
                    <button className="act-btn edit" onClick={() => { setRFIDPortalModal(u); setRFIDPortalCard(u.rfidCard || ''); }}><PencilSquareIcon /></button>
                    {u.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDPortalStaff(u._id)}><TrashIcon /></button>}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* ══ RFID STUDENTS TAB ══ */}
      {activeTab === 'rfid-students' && (
        <div className="panel">
          <div className="panel-head">
            <div><div className="panel-title">Student RFID Cards</div><div className="panel-sub">Assign RFID cards to students for gate entry logging</div></div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <select style={{fontFamily:'Sora,sans-serif',fontSize:'.78rem',padding:'7px 11px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none'}}
                value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSection(''); }}>
                <option value="">All Classes</option>
                {sortByName(classes).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <select style={{fontFamily:"Sora,sans-serif",fontSize:".78rem",padding:"7px 11px",border:"1.5px solid #e0e4f0",borderRadius:9,background:"#f8f9fc",outline:"none"}}
                value={filterSection} onChange={e => setFilterSection(e.target.value)} disabled={!filterClass}>
                <option value="">All Sections</option>
                {filterClass && sortAsc(classes.find(cls => cls._id === filterClass)?.sections || []).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <div className="search-wrap">
                <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input className="search-input" placeholder="Search students…" value={rfidStudSearch} onChange={e => setRFIDStudSearch(e.target.value)} />
              </div>
            </div>
          </div>
          {filteredRFIDStudents.length === 0 ? (
            <div className="bm-empty">No students found.</div>
          ) : (
            <>
            <div className="bm-tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr><th>Student</th><th>Admission No</th><th>Class</th><th>Section</th><th>RFID Card</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredRFIDStudents.map(s => (
                    <tr key={s._id}>
                      <td style={{fontWeight:600}}>{s.user?.name}</td>
                      <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>{s.admissionNo}</span></td>
                      <td><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.class?.name || '—'}</span></td>
                      <td><span style={{fontSize:'.74rem',background:'#eef2ff',color:'#3b6ef8',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.section}</span></td>
                      <td>
                        {s.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{s.rfidCard}</span>
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}
                      </td>
                      <td>
                        <button className="act-btn edit" onClick={() => { setRFIDStudModal(s); setRFIDStudCard(s.rfidCard || ''); }}><PencilSquareIcon /></button>
                        {s.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDStudent(s._id)}><TrashIcon /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bm-cards">
              {filteredRFIDStudents.map(s => (
                <div key={s._id} className="bm-card">
                  <div className="bmc-top">
                    <span style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{s.user?.name}</span>
                    {s.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{s.rfidCard}</span> : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}
                  </div>
                  <div className="bmc-row"><span className="bmc-label">Admission No</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>{s.admissionNo}</span></div>
                  <div className="bmc-row"><span className="bmc-label">Class</span><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.class?.name || '—'}</span></div>
                  <div className="bmc-row"><span className="bmc-label">Section</span><span style={{fontSize:'.74rem',background:'#eef2ff',color:'#3b6ef8',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{s.section}</span></div>
                  <div className="bmc-actions">
                    <button className="act-btn edit" onClick={() => { setRFIDStudModal(s); setRFIDStudCard(s.rfidCard || ''); }}><PencilSquareIcon /></button>
                    {s.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDStudent(s._id)}><TrashIcon /></button>}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* ══ RFID NON-PORTAL STAFF TAB ══ */}
      {activeTab === 'rfid-staff' && (
        <div className="panel">
          <div className="panel-head">
            <div><div className="panel-title">Non-Portal Staff RFID Cards</div><div className="panel-sub">Assign RFID cards to peon, cleaner, security, driver and other non-portal staff</div></div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <select style={{fontFamily:'Sora,sans-serif',fontSize:'.78rem',padding:'7px 11px',border:'1.5px solid #e0e4f0',borderRadius:9,background:'#f8f9fc',outline:'none'}}
                value={filterDesignation} onChange={e => setFilterDesignation(e.target.value)}>
                <option value="">All Designations</option>
                {sortAsc(DESIGNATIONS).map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</option>)}
              </select>
              <div className="search-wrap">
                <svg viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input className="search-input" placeholder="Search staff…" value={rfidStaffSearch} onChange={e => setRFIDStaffSearch(e.target.value)} />
              </div>
            </div>
          </div>
          {filteredRFIDStaff.length === 0 ? (
            <div className="bm-empty">No staff found. Add non-portal staff in Staff Management first.</div>
          ) : (
            <>
            <div className="bm-tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr><th>Name</th><th>Designation</th><th>Employee ID</th><th>RFID Card</th><th>Actions</th></tr></thead>
                <tbody>
                  {filteredRFIDStaff.map(s => (
                    <tr key={s._id}>
                      <td style={{fontWeight:600}}>{s.name}</td>
                      <td><span style={{fontSize:'.72rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 9px',borderRadius:99,fontWeight:600,textTransform:'capitalize'}}>{s.designation}</span></td>
                      <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>{s.employeeId || '—'}</span></td>
                      <td>
                        {s.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{s.rfidCard}</span>
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}
                      </td>
                      <td>
                        <button className="act-btn edit" onClick={() => { setRFIDStaffModal(s); setRFIDStaffCard(s.rfidCard || ''); }}><PencilSquareIcon /></button>
                        {s.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDStaff(s._id)}><TrashIcon /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bm-cards">
              {filteredRFIDStaff.map(s => (
                <div key={s._id} className="bm-card">
                  <div className="bmc-top">
                    <span style={{fontWeight:700,fontSize:'.84rem',color:'#0f1123'}}>{s.name}</span>
                    <span style={{fontSize:'.72rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 9px',borderRadius:99,fontWeight:600,textTransform:'capitalize'}}>{s.designation}</span>
                  </div>
                  <div className="bmc-row"><span className="bmc-label">Employee ID</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',color:'#6b7194'}}>{s.employeeId || '—'}</span></div>
                  <div className="bmc-row"><span className="bmc-label">RFID Card</span>{s.rfidCard ? <span className="rfid-badge"><CreditCardIcon />{s.rfidCard}</span> : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>Not assigned</span>}</div>
                  <div className="bmc-actions">
                    <button className="act-btn edit" onClick={() => { setRFIDStaffModal(s); setRFIDStaffCard(s.rfidCard || ''); }}><PencilSquareIcon /></button>
                    {s.rfidCard && <button className="act-btn del" onClick={() => handleRemoveRFIDStaff(s._id)}><TrashIcon /></button>}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      )}

      {/* ── RFID Portal Staff assign modal ── */}
      {rfidPortalModal && createPortal(
        <div className="modal-backdrop" onClick={() => setRFIDPortalModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Assign RFID Card — {rfidPortalModal.name}</div>
              <button className="modal-close" onClick={() => setRFIDPortalModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{background:'#f8f9fc',borderRadius:9,padding:'10px 12px',marginBottom:14,fontSize:'.78rem'}}>
              <div style={{fontWeight:600,color:'#0f1123',marginBottom:2}}>{rfidPortalModal.name}</div>
              <div style={{color:'#9094a8',textTransform:'capitalize'}}>{rfidPortalModal.role} · {rfidPortalModal.email}</div>
            </div>
            <div style={{background:'#eef2ff',border:'1px solid #c7d2fe',borderRadius:9,padding:'9px 12px',marginBottom:14,fontSize:'.76rem',color:'#3730a3'}}>
              📡 Once assigned, tapping the RFID card on the ZKTeco machine will auto check-in/out — same as teachers.
            </div>
            <div className="assign-field">
              <label className="assign-label">RFID Card Number *</label>
              <input className="assign-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                value={rfidPortalCard} onChange={e => setRFIDPortalCard(e.target.value)}
                placeholder="e.g. 0012345678" />
              <span style={{fontSize:'.68rem',color:'#b0b5cc'}}>Number printed on the back of the RFID card</span>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setRFIDPortalModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAssignRFIDPortalStaff} disabled={savingRFIDPortal}>
                {savingRFIDPortal ? 'Saving…' : 'Assign Card'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── RFID Staff assign modal ── */}
      {rfidStaffModal && createPortal(
        <div className="modal-backdrop" onClick={() => setRFIDStaffModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Assign RFID Card — {rfidStaffModal.name}</div>
              <button className="modal-close" onClick={() => setRFIDStaffModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{background:'#f8f9fc',borderRadius:9,padding:'10px 12px',marginBottom:14,fontSize:'.78rem'}}>
              <div style={{fontWeight:600,color:'#0f1123',marginBottom:2}}>{rfidStaffModal.name}</div>
              <div style={{color:'#9094a8',textTransform:'capitalize'}}>{rfidStaffModal.designation}{rfidStaffModal.employeeId ? ` · ${rfidStaffModal.employeeId}` : ''}</div>
            </div>
            <div style={{background:'#eef2ff',border:'1px solid #c7d2fe',borderRadius:9,padding:'9px 12px',marginBottom:14,fontSize:'.76rem',color:'#3730a3'}}>
              📡 Once assigned, this staff member can tap their RFID card on the ZKTeco machine to automatically check in and out.
            </div>
            <div className="assign-field">
              <label className="assign-label">RFID Card Number *</label>
              <input className="assign-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                value={rfidStaffCard} onChange={e => setRFIDStaffCard(e.target.value)}
                placeholder="e.g. 0012345678" />
              <span style={{fontSize:'.68rem',color:'#b0b5cc'}}>Number printed on the back of the RFID card</span>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setRFIDStaffModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAssignRFIDStaff} disabled={savingRFIDStaff}>{savingRFIDStaff ? 'Saving…' : 'Assign Card'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Fingerprint assign modal ── */}
      {assignModal && createPortal(
        <div className="modal-backdrop" onClick={() => setAssignModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Assign Machine ID {assignModal.uid || assignModal.userId}</div>
              <button className="modal-close" onClick={() => setAssignModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:16}}>Select which teacher this fingerprint belongs to.</div>
            <div className="assign-field">
              <label className="assign-label">Teacher *</label>
              <select className="assign-select" value={assignForm.teacherId} onChange={e => setAssignForm({teacherId:e.target.value})}>
                <option value="">Select teacher…</option>
                {teachers.map(t => <option key={t._id} value={t._id}>{t.user?.name || t.name} {t.employeeId ? `(${t.employeeId})` : ''}</option>)}
              </select>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setAssignModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAssign} disabled={assigning}>{assigning ? 'Saving…' : 'Assign'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── RFID Teacher assign modal ── */}
      {rfidTeachModal && createPortal(
        <div className="modal-backdrop" onClick={() => setRFIDTeachModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Assign RFID Card — {rfidTeachModal.user?.name}</div>
              <button className="modal-close" onClick={() => setRFIDTeachModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{fontSize:'.78rem',color:'#6b7194',marginBottom:16}}>
              Enter the card number printed on the back of the RFID card.
            </div>
            <div className="assign-field">
              <label className="assign-label">RFID Card Number *</label>
              <input className="assign-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                value={rfidTeachCard} onChange={e => setRFIDTeachCard(e.target.value)}
                placeholder="e.g. 0012345678" />
              <span style={{fontSize:'.68rem',color:'#b0b5cc'}}>Number printed on back of card</span>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setRFIDTeachModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAssignRFIDTeacher} disabled={savingRFIDTeach}>{savingRFIDTeach ? 'Saving…' : 'Assign Card'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── RFID Student assign modal ── */}
      {rfidStudModal && createPortal(
        <div className="modal-backdrop" onClick={() => setRFIDStudModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Assign RFID Card — {rfidStudModal.user?.name}</div>
              <button className="modal-close" onClick={() => setRFIDStudModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{background:'#f8f9fc',borderRadius:9,padding:'10px 12px',marginBottom:14,fontSize:'.78rem'}}>
              <div style={{fontWeight:600,color:'#0f1123',marginBottom:2}}>{rfidStudModal.user?.name}</div>
              <div style={{color:'#9094a8'}}>{rfidStudModal.admissionNo} · {rfidStudModal.class?.name} {rfidStudModal.section}</div>
            </div>
            <div className="assign-field">
              <label className="assign-label">RFID Card Number *</label>
              <input className="assign-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                value={rfidStudCard} onChange={e => setRFIDStudCard(e.target.value)}
                placeholder="e.g. 0012345678" />
              <span style={{fontSize:'.68rem',color:'#b0b5cc'}}>Number printed on back of card</span>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setRFIDStudModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAssignRFIDStudent} disabled={savingRFIDStud}>{savingRFIDStud ? 'Saving…' : 'Assign Card'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default BiometricSettings;